import type { PawsExperiments } from "./types";

/**
 * Quiz tests whose arm is stamped onto the customer across systems, so
 * retention and LTV can be cut by arm long after the test has ended.
 *
 * The tag string ({prefix}_{variant}, e.g. quiz_pv_test_b) is the join key
 * used IDENTICALLY on the Shopify order + customer, the Recharge subscription
 * + customer and the Klaviyo profile - never change one side alone
 * (paws-admin: internal/experiments/customertags.go customerTagConfigs;
 * paws-klaviyo-worker: src/planTest.ts QUIZ_TESTS).
 *
 * Only tests listed here are tagged. Concluded tests are removed so new
 * orders are never stamped into a finished cohort (quiz-plan-test, #125,
 * concluded 2026-10-22: its existing tags stay valid downstream).
 */
export const QUIZ_TESTS: Readonly<Record<string, { prefix: string; variants: readonly string[] }>> = {
    // Price & Value overhaul, paws-on-plates#51. a = control quiz, b = Pages A-D.
    "quiz-pv-test": { prefix: "quiz_pv_test", variants: ["a", "b"] },
};

export const PV_TEST_KEY = "quiz-pv-test";
export const PV_VARIANT = "b";

/** Signup cadence attribute, shared by every tagged test. */
export const SIGNUP_CADENCE_PROP = "_signup_cadence";

export type QuizTestTag = {
    /** Prefix, e.g. quiz_pv_test - also the Klaviyo profile property name. */
    test: string;
    /** Full arm string, e.g. quiz_pv_test_b. */
    arm: string;
    /** UTC ISO date (YYYY-MM-DD) of first assignment. */
    assignedAt: string;
};

/**
 * The visitor's tags for every running quiz test. Skips experiments that are
 * absent, unknown variants and QA overrides (QA previews must never land in
 * a cohort).
 */
export function quizTestTags(store: Readonly<PawsExperiments> | null | undefined): QuizTestTag[] {
    const tags: QuizTestTag[] = [];
    for (const [key, cfg] of Object.entries(QUIZ_TESTS)) {
        const rec = store?.assignments?.[key];
        if (!rec || rec.src === "qa_override") continue;
        if (!cfg.variants.includes(rec.variant)) continue;
        const at = new Date(Number(rec.at));
        if (Number.isNaN(at.getTime())) continue;
        tags.push({
            test: cfg.prefix,
            arm: `${cfg.prefix}_${rec.variant}`,
            assignedAt: at.toISOString().slice(0, 10),
        });
    }
    return tags;
}

/**
 * Line-item / cart-attribute properties for the given tags (empty object when
 * untagged). Leading underscore hides them from the customer; paws-admin
 * strips it when reading note attributes.
 */
export function quizTestProperties(tags: QuizTestTag[], planDays: number): Record<string, string> {
    if (!tags.length) return {};
    const props: Record<string, string> = {};
    for (const t of tags) {
        props[`_${t.test}`] = t.arm;
        props[`_${t.test}_assigned_at`] = t.assignedAt;
    }
    if (planDays > 0) props[SIGNUP_CADENCE_PROP] = String(planDays);
    return props;
}
